import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table, Card, Button, Typography, Tag, Space, Modal, Form, Input, Select, DatePicker,
  Popconfirm, App as AntApp, Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '@/services/api';
import { useSudoGate } from '@/hooks/useSudoGate';

const { Title } = Typography;

interface Plan {
  idx: string; name: string | null; product: string | null; period: string | null;
  periodUnit: number | null; quantity: number | null; enabled: boolean | null;
  start: string | null; expired: string | null; vendorIdx: string | null; vendorName: string | null;
}

export default function AdminLicensePlansPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { require: sudoReq, modal: sudoModal, sudoActive } = useSudoGate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Plan | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const plans = useQuery({
    queryKey: ['admin', 'licenses'],
    queryFn: async () => {
      const r = await apiClient.get<any>('/admin/licenses');
      return r as any;
    },
  });

  const vendors = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => {
      const r = await apiClient.get<any>('/admin/organizations');
      return r as any;
    },
  });

  const createMut = useMutation({
    mutationFn: (body: any) => apiClient.post('/admin/licenses', body),
    onSuccess: () => { message.success('Plan created'); setCreateOpen(false); createForm.resetFields(); qc.invalidateQueries({ queryKey: ['admin', 'licenses'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => apiClient.patch(`/admin/licenses/${id}`, body),
    onSuccess: () => { message.success('Plan updated'); setEditTarget(null); qc.invalidateQueries({ queryKey: ['admin', 'licenses'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/licenses/${id}`),
    onSuccess: () => { message.success('Plan disabled'); qc.invalidateQueries({ queryKey: ['admin', 'licenses'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('admin.licensePlans')}</Title>
        <Space>
          {sudoActive && <Tag color="orange">SUDO</Tag>}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>{t('admin.newPlan')}</Button>
        </Space>
      </Space>

      <Card>
        <Table<Plan> size="small" rowKey="idx" loading={plans.isLoading} dataSource={plans.data?.plans ?? []}
          pagination={{ pageSize: 30 }} columns={[
            { title: 'Vendor', dataIndex: 'vendorName', key: 'v', width: 150 },
            { title: 'Name', dataIndex: 'name', key: 'n' },
            { title: 'Product', dataIndex: 'product', key: 'p', width: 150 },
            { title: 'Period', key: 'pr', width: 110, render: (_, r) => r.period ? `${r.periodUnit ?? 1} ${r.period}` : '—' },
            { title: 'Qty', dataIndex: 'quantity', key: 'q', width: 70 },
            { title: 'Expires', key: 'ex', width: 120, render: (_, r) => r.expired ? dayjs(r.expired).format('YYYY-MM-DD') : '—' },
            { title: 'Status', key: 's', width: 90, render: (_, r) => r.enabled ? <Tag color="green">ON</Tag> : <Tag>OFF</Tag> },
            { title: 'Actions', key: 'a', width: 160, render: (_, r) => (
              <Space size="small">
                <Button size="small" icon={<EditOutlined />} onClick={() => { setEditTarget(r); editForm.setFieldsValue({ name: r.name, product: r.product, period: r.period, periodUnit: r.periodUnit, quantity: r.quantity, enabled: r.enabled, start: r.start ? dayjs(r.start) : null, expired: r.expired ? dayjs(r.expired) : null }); }}>{t('admin.edit')}</Button>
                {r.enabled && <Popconfirm title="Disable?" onConfirm={() => sudoReq(() => deleteMut.mutate(r.idx))}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
              </Space>
            )},
          ]}
        />
      </Card>

      <Modal title="Create license plan" open={createOpen} onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()} confirmLoading={createMut.isPending} okText="Create" width={600}>
        <Form form={createForm} layout="vertical" onFinish={(v) => {
          const body: any = { ...v };
          if (v.start) body.start = v.start.toISOString();
          if (v.expired) body.expired = v.expired.toISOString();
          createMut.mutate(body);
        }}>
          <Form.Item name="vendorIdx" label="Vendor" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={vendors.data?.vendors?.map((v: any) => ({ label: v.name, value: v.idx })) ?? []} />
          </Form.Item>
          <Form.Item name="name" label="Plan name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="product" label="Product ID"><Input /></Form.Item>
          <Form.Item name="period" label="Period"><Select allowClear options={['None','Day','Week','Month','Year'].map(v => ({ label: v, value: v }))} /></Form.Item>
          <Form.Item name="periodUnit" label="Period unit"><Input type="number" /></Form.Item>
          <Form.Item name="quantity" label="Max units"><Input type="number" /></Form.Item>
          <Form.Item name="start" label="Start"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="expired" label="Expires"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Edit license plan" open={!!editTarget} onCancel={() => setEditTarget(null)}
        onOk={() => editForm.submit()} confirmLoading={updateMut.isPending} okText="Save" width={600}>
        <Form form={editForm} layout="vertical" onFinish={(v) => {
          if (!editTarget) return;
          const body: any = {};
          for (const [k, val] of Object.entries(v)) {
            if (val === undefined || val === null) continue;
            if (k === 'start' || k === 'expired') { body[k] = (val as any).toISOString(); continue; }
            body[k] = val;
          }
          updateMut.mutate({ id: editTarget.idx, body });
        }}>
          <Form.Item name="name" label="Name"><Input /></Form.Item>
          <Form.Item name="product" label="Product"><Input /></Form.Item>
          <Form.Item name="period" label="Period"><Select allowClear options={['None','Day','Week','Month','Year'].map(v => ({ label: v, value: v }))} /></Form.Item>
          <Form.Item name="periodUnit" label="Period unit"><Input type="number" /></Form.Item>
          <Form.Item name="quantity" label="Max units"><Input type="number" /></Form.Item>
          <Form.Item name="start" label="Start date"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="expired" label="Expiry date"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="enabled" label="Enabled"><Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} /></Form.Item>
        </Form>
      </Modal>

      {sudoModal}
    </>
  );
}
